import React, { useEffect, useMemo, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import { CircleInfoIcon, BookIcon, CartShoppingIcon } from '@revfluence/fresh-icons/regular/esm';
import { Loader2 } from 'lucide-react';
import { useHistory } from 'react-router-dom';

import { useGetCatalogs } from '@frontend/app/containers/Settings/ProductFulfillment/Catalogs/hooks/useGetCatalogs';
import { useGetSelectionCriteria } from '@frontend/app/containers/Settings/ProductFulfillment/Catalogs/hooks/useGetSelectionCriteria';
import { useGetProjectConfigByType } from '@frontend/applications/ProductFulfillmentApp/hooks/useGetProjectConfigByType';
import { GET_PROJECT_CONFIG_BY_TYPE_QUERY } from '@frontend/applications/ProductFulfillmentApp/queries';
import { ProjectConfigType } from '@frontend/applications/ProductFulfillmentApp/types/globalTypes';
import { GetProjectConfigByProjectIdAndType } from '@frontend/applications/ProductFulfillmentApp/queries/types/GetProjectConfigByProjectIdAndType';
import { pfaV2Routes } from '@frontend/app/containers/Settings/ProductFulfillment/routes';
import { Button } from '@/shadcn/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shadcn/components/ui/select';
import { Label } from '@/shadcn/components/ui/label';

// Stable empty array references to prevent infinite loops
const EMPTY_ARRAY: any[] = [];
const EMPTY_CATALOGS: any[] = [];

interface CatalogSelectionSectionV2Props {
  selectedCatalog: number | undefined;
  setSelectedCatalog: (value: number) => void;
  selectedRule: number | undefined;
  setSelectedRule: (value: number | null) => void;
  projectId: number;
  onCriteriaChange?: (criteria: any[]) => void;
}

export const CatalogSelectionSectionV2: React.FC<CatalogSelectionSectionV2Props> = ({
  selectedCatalog,
  setSelectedCatalog,
  selectedRule,
  setSelectedRule,
  projectId,
  onCriteriaChange,
}) => {
  const apolloClient = useApolloClient();

  // Get catalogs with stable fallback
  const catalogsHookResult = useGetCatalogs();
  const catalogs = useMemo(() => {
    const hookCatalogs = catalogsHookResult.catalogs;
    if (!hookCatalogs || hookCatalogs.length === 0) {
      return EMPTY_CATALOGS;
    }
    return hookCatalogs;
  }, [catalogsHookResult.catalogs]);
  const catalogsLoading = catalogsHookResult.loading;
  const history = useHistory();
  const prevCallbackRef = useRef(onCriteriaChange);

  // Get project config to check for available catalogs
  const projectConfigFromCache = apolloClient.readQuery<GetProjectConfigByProjectIdAndType>({
    query: GET_PROJECT_CONFIG_BY_TYPE_QUERY,
    variables: {
      projectId: Number(projectId),
      type: ProjectConfigType.ProductCatalog,
    },
  });

  const { projectConfig, loading: configLoading } = useGetProjectConfigByType({
    variables: {
      projectId: Number(projectId),
      type: ProjectConfigType.ProductCatalog,
    },
    skip: !projectId,
  }) || { projectConfig: undefined, loading: false };

  // Use cache or fetched config
  const availableCatalogIds = useMemo(() =>
    projectConfigFromCache?.projectConfig?.brandCatalogs ||
    projectConfig?.brandCatalogs ||
    EMPTY_ARRAY,
    [projectConfigFromCache?.projectConfig?.brandCatalogs, projectConfig?.brandCatalogs]
  );

  // Filter catalogs based on project config
  const filteredCatalogs = useMemo(() =>
    catalogs.filter((catalog) => availableCatalogIds.includes(catalog.id)),
    [catalogs, availableCatalogIds]
  );

  // Get criteria with stable fallback
  const criteriaHookResult = useGetSelectionCriteria({
    variables: {
      brandCatalogId: selectedCatalog,
    },
    skip: !selectedCatalog,
  });
  const criteria = useMemo(() => {
    const hookCriteria = criteriaHookResult?.criteria;
    if (!hookCriteria || hookCriteria.length === 0) {
      return EMPTY_ARRAY;
    }
    return hookCriteria;
  }, [criteriaHookResult?.criteria]);
  const isCriteriaLoading = criteriaHookResult?.loading || false;

  const isLoading = catalogsLoading || configLoading;

  // Auto-select effects
  useEffect(() => {
    // Only auto-select when data has finished loading and we have exactly one catalog
    if (!catalogsLoading && !configLoading && filteredCatalogs.length === 1 && !selectedCatalog) {
      setSelectedCatalog(Number(filteredCatalogs[0].id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCatalogs, selectedCatalog, catalogsLoading, configLoading]);

  useEffect(() => {
    // Only auto-select when data has finished loading and we have exactly one rule
    if (!isCriteriaLoading && criteria?.length === 1 && !selectedRule && selectedCatalog) {
      setSelectedRule(Number(criteria[0].id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteria, selectedRule, selectedCatalog, isCriteriaLoading]);

  // Notify parent component of criteria changes
  useEffect(() => {
    prevCallbackRef.current = onCriteriaChange;
    if (onCriteriaChange && !isCriteriaLoading) {
      onCriteriaChange(criteria || EMPTY_ARRAY);
    }
  }, [criteria, isCriteriaLoading, onCriteriaChange]);

  const handleSetupCatalogClick = () => {
    history.push(pfaV2Routes.projectSettings.productCatalogs.replace(':projectId', String(projectId)));
  };

  return (
    <>
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <BookIcon className="w-4 h-4" />
          Send Product Catalog
        </Label>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : filteredCatalogs.length > 0 ? (
          <Select
            value={selectedCatalog ? String(selectedCatalog) : undefined}
            onValueChange={(value) => {
              setSelectedCatalog(Number(value));
              setSelectedRule(null);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a Catalog" />
            </SelectTrigger>
            <SelectContent>
              {filteredCatalogs.map((catalog: any) => (
                <SelectItem key={catalog.id} value={String(catalog.id)}>
                  {catalog.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="p-6 bg-[#FFF9F0] rounded-lg">
            <div className="flex items-start gap-3 mb-2">
              <CircleInfoIcon className="w-6 h-6 text-[#F5A623] flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Setup Catalog
                </h3>
                <p className="text-gray-600 mb-4">
                  Create your Product Catalog and provide instructions for how to choose products for this collaboration.
                </p>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                className="bg-[#4285F4] hover:bg-[#3367D6] text-white px-6 py-2 rounded-lg"
                onClick={handleSetupCatalogClick}
              >
                Set up Catalog
              </Button>
            </div>
          </div>
        )}
      </div>

      {selectedCatalog && (
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <CartShoppingIcon className="w-4 h-4" />
            Send Product Selection Rule
          </Label>
          <Select
            value={selectedRule ? String(selectedRule) : undefined}
            onValueChange={(value) => setSelectedRule(value ? Number(value) : null)}
            disabled={!selectedCatalog || (!isCriteriaLoading && !criteria?.length)}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={isCriteriaLoading ? 'Loading...' : !selectedCatalog || criteria?.length ? 'Select a Rule' : 'No rules available'}
              />
            </SelectTrigger>
            <SelectContent>
              {criteria?.map((rule: any) => (
                <SelectItem key={rule.id} value={String(rule.id)}>
                  {rule.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isCriteriaLoading && (
            <div className="flex items-center justify-center py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          )}
        </div>
      )}
    </>
  );
};
